import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Instagram, Printer } from "lucide-react";
import { api } from "../lib/api";
import { BUSINESS } from "../lib/data";
import logoFull from "../assets/releaf_logo_full.png";

export default function GiftCertificate() {
  const { giftId } = useParams();
  const [gift, setGift] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/gift-certificates/${giftId}`).then((res) => setGift(res.data)).catch(() => setMissing(true));
  }, [giftId]);

  if (missing) return <main className="min-h-screen bg-ivory p-12 text-center text-moss">Gift certificate not found. <Link to="/gift" className="font-semibold text-forest">Create one</Link>.</main>;
  if (!gift) return <main className="min-h-screen bg-ivory p-12 text-center text-moss">Loading...</main>;

  const pending = gift.status === "pending_payment";

  return (
    <main data-testid="gift-certificate-page" className="min-h-screen bg-cream px-4 py-10 print:bg-white print:p-0">
      <div className="mx-auto mb-6 flex max-w-3xl flex-wrap items-center justify-between gap-3 print:hidden">
        <Link to="/" className="text-sm font-medium text-moss hover:text-forest">&larr; Back to Releaf</Link>
        <div className="flex gap-3">
          <Link to="/gift" data-testid="gift-create-another" className="rounded-md border border-linen px-4 py-2 text-sm font-semibold text-forest hover:border-forest">Create another</Link>
          <button data-testid="gift-print-button" onClick={() => window.print()} className="flex items-center gap-2 rounded-md bg-forest px-4 py-2 text-sm font-semibold text-ivory hover:bg-forest-dark"><Printer size={15} /> Print</button>
        </div>
      </div>

      {pending && (
        <p data-testid="gift-pending-notice" className="mx-auto mb-6 max-w-3xl rounded-md border border-gold/50 bg-ivory px-5 py-4 text-sm text-forest print:hidden">
          <strong>Almost done:</strong> this certificate activates once payment is received. Pay at Salon 406 or call/text {BUSINESS.phone}. We&rsquo;ve emailed a copy to {gift.purchaser_email}.
        </p>
      )}

      <article data-testid="gift-certificate" className="mx-auto max-w-3xl bg-sage/30 p-6 sm:p-8 print:bg-white print:p-4">
        <div className="bg-ivory px-8 py-10 sm:px-14 sm:py-12">
          <img src={logoFull} alt="Releaf Wellness & Aesthetics — Nichole Olsen, Nurse Practitioner" className="mx-auto h-44 w-auto sm:h-52" />

          <div className="mt-8 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-moss/80 px-4 py-2.5 text-ivory"><span className="text-xs font-semibold uppercase tracking-[0.15em]">To:</span> <span data-testid="gift-recipient" className="ml-2 font-serif text-lg">{gift.recipient_name}</span></div>
              <div className="bg-moss/80 px-4 py-2.5 text-ivory"><span className="text-xs font-semibold uppercase tracking-[0.15em]">From:</span> <span className="ml-2 font-serif text-lg">{gift.purchaser_name}</span></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-moss/80 px-4 py-2.5 text-ivory"><span className="text-xs font-semibold uppercase tracking-[0.15em]">Amount:</span> <span data-testid="gift-value" className="ml-2 font-serif text-lg">{gift.kind === "amount" ? `$${gift.amount}` : gift.service_name}</span></div>
              <div className="bg-moss/80 px-4 py-2.5 text-ivory"><span className="text-xs font-semibold uppercase tracking-[0.15em]">Valid until:</span> <span data-testid="gift-valid-until" className="ml-2 font-serif text-lg">{gift.valid_until || "—"}</span></div>
            </div>
          </div>

          {gift.message && <p className="mx-auto mt-6 max-w-md text-center text-sm italic leading-relaxed text-moss">&ldquo;{gift.message}&rdquo;</p>}

          <p className="mt-8 text-center font-serif text-3xl font-medium uppercase tracking-[0.1em] text-gold-dark sm:text-4xl">Gift Certificate</p>
          <p className="mt-4 flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-forest"><Instagram size={14} /> {BUSINESS.instagram}</p>

          <div className="mt-8 flex flex-wrap items-end justify-between gap-4 border-t border-linen pt-4 text-[11px] text-moss">
            <p>Code <span data-testid="gift-code" className="font-semibold text-forest">{gift.code}</span> &middot; Status: <span className="font-semibold text-forest">{pending ? "Pending payment" : gift.status}</span></p>
            <p>Issued {gift.created_at.slice(0, 10)}</p>
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-forest sm:flex-row sm:items-end sm:justify-between">
          <p className="max-w-sm">Terms &amp; Conditions: Gift certificate is redeemable for goods &amp; services only. Cannot be traded in for cash.</p>
          <p className="text-right">{BUSINESS.phone}<br />{BUSINESS.addressLines.join(", ")}</p>
        </div>
      </article>
    </main>
  );
}
